import { readOnly } from '@ember/object/computed';
import { computed } from '@ember/object';
import BillingRecord from "./billable-record";
import { attr, belongsTo, hasMany } from '@ember-data/model';
import { USER } from "./user";

const DEFAULT_INCLUDES = (user) => {
  switch (user.get('type')) {
    case USER.TYPES.BUYER:
      return [
        'expedite-requestor',
        'user',
        'sh-invoice.sh-invoice-line-items',
        'sh-payment-line-items.application',
        'sh-payment-line-items.sh-payment-line-items',
      ];
    case USER.TYPES.SELLER:
      return [
        'expedite-requestor',
        'fee-structure',
        'user',
        'user.preferred-payout-account',
        'sh-payment-line-items.application',
        'sh-payment-line-items.sh-payment-line-items',
        'sh-payment-transactions',
      ];
    case USER.TYPES.ADMIN:
      return [
        'expedite-requestor',
        'fee-structure',
        'user',
        'sh-invoice.sh-invoice-line-items',
        'user.preferred-payout-account',
        'sh-payment-line-items.application',
        'sh-payment-line-items.sh-payment-line-items',
        'sh-payment-transactions',
      ];
  }
};

const PROCESSOR_TYPES = {
  PAYPAL : 'paypal',
  PAYONEER : 'payoneer',
  PAYONEER_MANUAL : 'payoneer_manual',
  ZERO : 'zero',
};

const SIMPLE_STATUS = {
  BANK : 'bank',
  MANUAL : 'manual',
  PAYONEER : 'payoneer',
  PAYPAL : 'paypal',
  PENDING : 'pending',
  HYPERWALLET: 'hyperwallet',
};

const STATUS = {
  DUE: 'Due',
  PAID: 'Paid',
  ERROR: 'Error',
  UNKNOWN: 'Unknown'
};

export const SH_PAYMENT = {
  DEFAULT_INCLUDES,
  PROCESSOR_TYPES,
  SIMPLE_STATUS,
  STATUS,
};

const ShPayment = BillingRecord.extend({
  approved: attr('boolean'),
  agreement: attr('number'),
  estimatedPayoutDate: attr('date'),
  failureCode: attr('string'),
  paymentProcessorType: attr('string'), // @type {String}   of SH_PAYMENT.PROCESSOR_TYPES

  //relationships
  actions: hasMany('action', {
    async: false,
    inverse: 'actionable',
  }),
  application: belongsTo('application', { async: false }),
  expediteRequestor: belongsTo('user', { async: false }),
  feeStructure: belongsTo('feeStructure', { async: false }),
  shInvoice: belongsTo('shInvoice', { async: false }),
  shPaymentLineItems: hasMany('shPaymentLineItem', { async: false }),
  shPaymentTransactions: hasMany('shPaymentTransaction', { async: false }),
  user: belongsTo('user', { async: false }),

  /**
   * Convenience method to return our related billable line items.  Named the same so that we can call this method
   * on either ShPayment or ShInvoices if we don't care which type is returned.
   * @return {Ember.A<ShBillableLineItem>}
   */
  shBillableLineItems: readOnly('shPaymentLineItems'),

  /**
   * Nifty trick that lets us call .shPayment on both ShInvoices and ShPayments, and get either the related ShPayment
   * (in the case of an ShInvoice) or the identity (as in the case right here).
   */
  shPayment: computed(
    'this',
    function() {return this;}
  ),

  /**
   * Returns a path to download a pdf of this billable
   * @type {String}
   */
  downloadPath: computed(
    'id',
    function () {
      return `/sh-payments/${this.get('id')}/pdf`;
    }
  ),

  /**
   * @type {Boolean}    True only for ShPayment since the other BillingRecord types don't have paymentProcessor types.
   */
  hasPaymentProcessorType: true,

  /**
   * Calculate the fee for a seller to expedite this payment
   */
  sellerExpediteFee: computed(
    'shPaymentLineItems.@each.{amount,isExpediteFee}',
    function () {
      const total = this.get('shPaymentLineItems').reduce((carry, shPaymentLineItem) => {
        if (shPaymentLineItem.get('isExpediteFee')) {
          return carry;
        }
        return carry + shPaymentLineItem.get('amount');
      }, 0);

      return (total * this.get('feeStructure.sellerAdvanceFee')).toFixed(2);
    }
  ),

  /**
   * @type {String}     of type SH_PAYMENT.STATUS.*
   */
  paymentStatusText: computed(
  'dueAt',
  'failureCode',
  'paidAt',
  function() {
    let failureCode = this.get('failureCode'),
      dueAt = this.get('dueAt'),
      paidAt = this.get('paidAt');

    if (paidAt) {
      return SH_PAYMENT.STATUS.PAID;
    }

    if (failureCode) {
      return SH_PAYMENT.STATUS.ERROR;
    }

    if (dueAt) {
      return SH_PAYMENT.STATUS.DUE;
    }

    return SH_PAYMENT.STATUS.UNKNOWN;
  }),
});

export default ShPayment;
